import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface StoryCreationInputProps {
    onCreate: (title: string) => void;
    onCancel: () => void;
}

export const StoryCreationInput: React.FC<StoryCreationInputProps> = React.memo(({ onCreate, onCancel }) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    React.useLayoutEffect(() => {
        // Force focus synchronously
        if (inputRef.current) {
            inputRef.current.focus();
        }
        // Fallback: Force focus again after paint to beat any race conditions
        const timer = setTimeout(() => {
            if (inputRef.current) inputRef.current.focus();
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Stop event from bubbling to global listeners
        e.stopPropagation();

        if (e.key === 'Enter' && title.trim()) {
            onCreate(title);
        }
        if (e.key === 'Escape') {
            onCancel();
        }
    };

    const handleContainerClick = (e: React.MouseEvent) => {
        // Prevent clicks from freezing the app
        e.stopPropagation();
    };

    return (
        <div
            data-testid="story-creation-input"
            onClick={handleContainerClick}
            style={{ display: 'flex', gap: '5px', alignItems: 'center', position: 'relative', zIndex: 1000 }}
        >
            <Input
                data-testid="story-creation-title"
                ref={inputRef}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('logger.storyCreation.placeholder')}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                className="h-8 min-w-[200px] bg-[var(--color-input-bg)] text-sm allow-select"
                autoFocus
            />
            <Button
                data-testid="story-creation-submit"
                size="sm"
                className="bg-[var(--color-data-story)] text-black hover:bg-[var(--color-data-story)]/80"
                onClick={(e) => {
                    e.stopPropagation();
                    if (title.trim()) onCreate(title);
                }}
            >
                {t('logger.storyCreation.create')}
            </Button>
            <Button
                data-testid="story-creation-cancel"
                variant="secondary"
                size="icon-sm"
                onClick={(e) => {
                    e.stopPropagation();
                    onCancel();
                }}
            >
                ✕
            </Button>
        </div>
    );
});
